// React imports not needed - using JSX transform
import type { ProductSummary } from '@arm-pd/domain';

interface ProductCardProps {
  product: ProductSummary;
  onSelect?: (product: ProductSummary) => void;
  isSelected?: boolean;
}

/**
 * Product card component displaying product summary
 * Touch-friendly for iPad and event displays
 */
export function ProductCard({ product, onSelect, isSelected = false }: ProductCardProps) {
  // Get default price
  const defaultPrice = product.prices?.find(p => p.isDefault) || product.prices?.[0];

  // Format price
  const formattedPrice = defaultPrice
    ? new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: defaultPrice.currencyIsoCode || 'GBP',
        minimumFractionDigits: 2,
      }).format(defaultPrice.price)
    : null;

  return (
    <div
      className={`
        group relative rounded-lg border transition-all duration-200
        ${
          isSelected
            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
            : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
        }
      `}
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(product)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(product);
        }
      }}
      aria-pressed={isSelected}
    >
      <div className="p-6">
        {/* Product name */}
        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-700">
          {product.name}
        </h3>

        {/* Product code */}
        {product.code && (
          <p className="mt-1 text-sm text-gray-500">
            Code: <span className="font-mono">{product.code}</span>
          </p>
        )}

        {/* Price */}
        <div className="mt-4 flex items-baseline">
          {formattedPrice ? (
            <span className="text-2xl font-bold text-gray-900">{formattedPrice}</span>
          ) : (
            <span className="text-sm text-gray-500 italic">Price on request</span>
          )}
        </div>

        {/* Price book info */}
        {defaultPrice && (
          <p className="mt-1 text-xs text-gray-500">{defaultPrice.currencyIsoCode}</p>
        )}

        {/* Selling model badge */}
        {product.sellingModel && (
          <div className="mt-4">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
              {product.sellingModel.name}
            </span>
          </div>
        )}

        {/* Status indicator */}
        <div className="mt-4 flex items-center gap-2">
          <span
            className={`inline-flex h-2 w-2 rounded-full ${
              product.isActive ? 'bg-green-500' : 'bg-gray-300'
            }`}
            aria-hidden="true"
          />
          <span className="text-xs text-gray-600">{product.isActive ? 'Active' : 'Inactive'}</span>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute right-4 top-4">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                clipRule="evenodd"
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Footer action */}
      <div className="border-t border-gray-200 px-6 py-4">
        <button
          type="button"
          className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-min-height"
        >
          View Details
        </button>
      </div>
    </div>
  );
}
